import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, mediaSrc, mediaSrcById, fmtTime } from "../api";
import { playVoice } from "../lib/sounds";

import {
  getVoiceSpeed, fmtSecs, subscribeVoice, playVoiceNote, pauseVoiceNote, seekVoiceNote, setVoiceRate, setVoiceInView,
} from "../lib/voicePlayer";

/** Voice note player — view over the GLOBAL player (lib/voicePlayer.js):
 *  local blob + custom controls (iOS "Hey Buddy" recognizer safety: every play()
 *  first takes the playback focus) and playback keeps going after leaving the
 *  chat (Shell shows VoiceMiniPlayer). */
function VoiceNotePlayer({ media, durationHint, messageId, convId, senderName, createdAt }) {
  const wrapRef = useRef(null);
  const [s, setS] = useState(null);
  useEffect(() => subscribeVoice(setS), []);
  const active = s?.id === messageId;
  const playing = active && s.playing;
  const loading = active && s.loading;
  const cur = active ? s.cur : 0;
  const dur = active && s.dur > 0 ? s.dur : Number(durationHint) || 0;

  // Active note: tell the global player whether this bubble is on screen
  // (scrolled away -> Shell shows the mini bar inside the chat too)
  useEffect(() => {
    const el = wrapRef.current;
    if (!active || !el) return undefined;
    const io = new IntersectionObserver(([en]) => setVoiceInView(messageId, !!en?.isIntersecting), { threshold: 0.2 });
    io.observe(el);
    const onScrollTo = () => el.scrollIntoView({ block: "center", behavior: "smooth" });
    window.addEventListener("osc-voice-scroll-to", onScrollTo);
    return () => { io.disconnect(); window.removeEventListener("osc-voice-scroll-to", onScrollTo); setVoiceInView(messageId, false); };
  }, [active, messageId]);

  const play = async () => {
    try {
      await playVoiceNote({
        id: messageId, convId, sender: senderName, media, durationHint, createdAt, inView: true,
        onEnded: () => {
          // WhatsApp-style chain: play the next voice note loaded in this thread
          const mine = wrapRef.current;
          if (!mine) return false;
          const wraps = Array.from(document.querySelectorAll("[data-voice-note]"));
          const next = wraps[wraps.indexOf(mine) + 1];
          if (!next) return false;
          try {
            next.scrollIntoView({ block: "nearest", behavior: "smooth" });
            next.dispatchEvent(new CustomEvent("osc-voice-play"));
            return true;
          } catch (err) { return false; }
        },
      });
    } catch (err) {
      toast.error("Audio failed");
    }
  };

  // Chained playback support: previous note dispatches "osc-voice-play" on us
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const h = () => play();
    el.addEventListener("osc-voice-play", h);
    return () => el.removeEventListener("osc-voice-play", h);
  });

  const seek = (e) => {
    e.stopPropagation();
    if (!active) return;
    const rect = e.currentTarget.getBoundingClientRect();
    seekVoiceNote((e.clientX - rect.left) / rect.width);
  };

  const pct = dur > 0 ? Math.min(100, (cur / dur) * 100) : 0;

  return (
    <div ref={wrapRef} data-voice-note className="relative flex-1 min-w-0 flex items-center gap-2 max-w-[260px]">
      <button
        type="button"
        data-testid={playing ? "voice-note-pause-btn" : "voice-note-load-btn"}
        onClick={(e) => { e.stopPropagation(); playing ? pauseVoiceNote() : play(); }}
        className="shrink-0 h-9 w-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center active:scale-95 transition-transform"
        aria-label={playing ? "pause voice note" : "play voice note"}
      >
        {loading ? (
          <span className="h-4 w-4 rounded-full border-2 border-current border-t-transparent animate-spin" />
        ) : playing ? (
          <Pause className="h-4 w-4" />
        ) : (
          <Play className="h-4 w-4 ml-0.5" />
        )}
      </button>
      <div className="flex-1 min-w-0">
        <div
          data-testid="voice-note-progress"
          role="slider"
          aria-valuenow={Math.round(pct)}
          onClick={seek}
          className="h-6 flex items-center cursor-pointer"
        >
          <div className="relative h-1.5 w-full rounded-full bg-muted-foreground/25">
            <div className="absolute inset-y-0 left-0 rounded-full bg-primary" style={{ width: `${pct}%` }} />
            <div className="absolute top-1/2 -translate-y-1/2 h-3 w-3 rounded-full bg-primary shadow" style={{ left: `calc(${pct}% - 6px)` }} />
          </div>
        </div>
        <div className="text-[11px] tabular-nums text-muted-foreground leading-none -mt-0.5" data-testid="voice-note-time">
          {fmtSecs(playing || cur > 0 ? cur : dur)}
        </div>
      </div>
    </div>
  );
}
import { useApp } from "../context/AppContext";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { StatusChip, PriorityBadge } from "./Artifacts";
import { TextOverlays } from "./TextOverlays";
import { PdfViewer } from "./PdfViewer";
import { PdfThumbnail } from "./PdfThumbnail";
import { PollBubble } from "./PollBubble";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "./ui/alert-dialog";
import { useLongPress } from "../lib/longPress";
import { MessageActionsSheet } from "./MessageActionsSheet";
import { ForwardDialog } from "./ForwardDialog";
import { EmojiPickerDialog } from "./EmojiPickerDialog";
import { ReactionsSheet } from "./ReactionsSheet";
import { MessageInfoSheet } from "./MessageInfoSheet";
import { MessageStickerStudio } from "./MessageStickerStudio";
import { LocalizedSticker } from "./LocalizedSticker";
import { AckStatusDialog } from "./AckStatusDialog";
import { MeasurePhotos } from "./MeasurePhotos";
import { TrimReportBubble } from "./TrimReportBubble";
import { hapticTick, hapticSelect } from "../lib/haptics";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { saveToDevice, shareNative, mediaFilename } from "../lib/share";
import { toast } from "sonner";
import {
  AlertTriangle, Ruler, Package, HelpCircle, Gavel, MoreVertical, FileText, CheckSquare, MapPin, Mic, Briefcase, Lock, Megaphone, Link2, Check, CheckCheck, Languages, CheckCircle2, RotateCcw, Reply, AtSign, Play, Pause, Trash2, Forward, Ban, Share2, SmilePlus, Info, Pencil, Unlink, Sticker as StickerIcon,
} from "lucide-react";

const chatDraftLabel = (d, t) => {
  switch (d.type) {
    case "draft_message": return `${t("buddy_to")}: ${d.recipient_name || ""}`;
    case "draft_group_message": return `${t("buddy_group")}: ${d.group_name || ""}`;
    case "draft_task": return t("buddy_new_task");
    case "draft_issue": return t("buddy_new_issue");
    case "draft_memory": return t("buddy_new_memory");
    case "draft_knowledge": return d.op === "delete" ? t("buddy_know_delete") : t("buddy_know_update");
    case "draft_intro": return t("buddy_intro_draft");
    case "draft_delete_conversation": return t("buddy_delete_conv");
    default: return t("buddy_confirm");
  }
};

const QUICK_REACTIONS = ["\u{1F44D}", "\u{2705}", "\u{1F44C}", "\u{1F6A8}", "\u{2753}"];

/** Small round profile photo shown next to messages from OTHERS in group chats
 *  (falls back to colored initials when the user has no photo). */
/** Tiny play/stop button on Buddy's messages: re-speaks the text with Buddy's voice. */
export function BuddySpeakButton({ text, userId }) {
  const [state, setState] = useState("idle"); // idle | loading | playing
  const handleRef = useRef(null);
  const toggle = async (e) => {
    e.stopPropagation();
    if (state === "playing") { try { handleRef.current?.stop?.(); handleRef.current?.pause?.(); } catch (er) { /* ignore */ } setState("idle"); return; }
    if (state === "loading") return;
    setState("loading");
    try {
      const qs = `?user_id=${encodeURIComponent(userId)}&text=${encodeURIComponent(text.slice(0, 600))}`;
      const r = await api.get(`/buddy/tts-rest/replay${qs}`);
      const b64 = r.data?.audio_base64;
      if (!b64) { setState("idle"); return; }
      setState("playing");
      handleRef.current = await playVoice(b64, { onEnd: () => setState("idle") });
    } catch (er) { setState("idle"); }
  };
  return (
    <button type="button" onClick={toggle} aria-label="play" data-testid="buddy-msg-play"
      className="h-5 w-5 rounded-full bg-primary/10 text-primary flex items-center justify-center active:scale-95 mr-0.5">
      {state === "loading" ? <span className="h-2.5 w-2.5 rounded-full border-2 border-primary/40 border-t-primary animate-spin" />
        : state === "playing" ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3 ml-px" />}
    </button>
  );
}

export function SenderAvatar({ sender, className = "h-7 w-7" }) {
  const initials = (sender?.name || "?").split(" ").filter(Boolean).map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  return (
    <Avatar className={`${className} shrink-0`} data-testid="message-sender-avatar">
      {sender?.avatar_media_id ? (
        <AvatarImage src={mediaSrcById(sender.avatar_media_id)} alt="" className="object-cover" />
      ) : null}
      <AvatarFallback style={{ backgroundColor: sender?.avatar_color || "#64748b", color: "#fff" }} className="text-[10px] font-semibold">
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}

export function MessageBubble({
  message, sender, conversation, onOpenPhoto, onOpenVideo, onToggleDecision, onIssueStatus,
  selectMode, selected, onToggleSelect, onStartSelect, onCreateIssue, onCreateTask, onShareWithJob, onUnlink,
  onReact, reads, delivers, onResolveIssue, onReply, onQuoteJump, onSaveToFiles, onDelete,
  isStarred, onToggleStar, onPin, highlightQuery, onEdit,
}) {
  const { user, t, lang } = useApp();
  const navigate = useNavigate();
  const [showOriginal, setShowOriginal] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [forwardOpen, setForwardOpen] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [reactionsOpen, setReactionsOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [stickerMedia, setStickerMedia] = useState(null); // photo/video of this message → Sticker Studio
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [hiddenLocally, setHiddenLocally] = useState(false); // "deleted for me" (no reload needed)
  const [voiceSpeed, setVoiceSpeed] = useState(getVoiceSpeed);
  // Buddy text-chat draft buttons (Confirm / Cancel inside the bubble)
  const [chatDraftStatus, setChatDraftStatus] = useState(null);
  const [chatDraftBusy, setChatDraftBusy] = useState(false);
  const resolveChatDraft = async (op) => {
    if (chatDraftBusy) return;
    setChatDraftBusy(true);
    try {
      const { data } = await api.post("/buddy/chat-draft", { user_id: user.id, message_id: message.id, op });
      setChatDraftStatus(data.status || (op === "confirm" ? "done" : "cancelled"));
    } catch (e) {
      toast.error(t("something_wrong"));
    } finally {
      setChatDraftBusy(false);
    }
  };
  // "Entendi" (mandatory acknowledgment on broadcast notices)
  const [ackBusy, setAckBusy] = useState(false);
  const [ackLocal, setAckLocal] = useState(null); // optimistic ack before WS confirms
  const [acksOpen, setAcksOpen] = useState(false);
  const isOwn = message.sender_id === user.id;
  const canManageIssues = ["supervisor", "office", "manager", "admin"].includes(user.role);
  const longPress = useLongPress(() => { if (!selectMode) { hapticTick("start"); setSheetOpen(true); } });

  // Quick reaction: double-tap (mobile) / double-click (desktop) = instant 👍
  // Skipped on interactive elements (photos, players, links, buttons).
  const lastTapRef = useRef(0);
  const isInteractiveTarget = (e) =>
    !!(e.target && e.target.closest && e.target.closest("[data-testid='message-photo'],video,audio,a,button,[data-voice-audio]"));
  const quickLike = (e) => {
    if (selectMode || !onReact || isInteractiveTarget(e)) return;
    onReact(message, "👍");
  };
  const handleBubbleTouchEnd = (e) => {
    longPress.onTouchEnd(e);
    if (selectMode) return;
    const now = Date.now();
    if (now - lastTapRef.current < 320) {
      lastTapRef.current = 0;
      quickLike(e);
    } else {
      lastTapRef.current = now;
    }
  };

  // ---- Swipe-to-reply (WhatsApp style) ----
  // Other people's messages: drag RIGHT to reply. Your own: drag LEFT.
  const swipeRef = useRef({ x0: 0, y0: 0, dx: 0, horiz: false });
  const swipeBodyRef = useRef(null);
  const swipeIconRef = useRef(null);

  const onSwipeTouchStart = (e) => {
    const t0 = e.touches?.[0];
    if (t0) swipeRef.current = { x0: t0.clientX, y0: t0.clientY, dx: 0, horiz: false };
    longPress.onTouchStart(e);
  };
  const onSwipeTouchMove = (e) => {
    const t0 = e.touches?.[0];
    const s = swipeRef.current;
    if (t0 && !selectMode && onReply) {
      const dxRaw = t0.clientX - s.x0;
      const dy = t0.clientY - s.y0;
      if (!s.horiz && Math.abs(dxRaw) > 14 && Math.abs(dxRaw) > Math.abs(dy) + 8) s.horiz = true;
      if (s.horiz) {
        longPress.onTouchCancel(e); // horizontal gesture — not a long-press
        const dx = isOwn ? Math.min(0, dxRaw) : Math.max(0, dxRaw); // only toward the center
        const capped = Math.max(-88, Math.min(88, dx));
        s.dx = capped;
        if (swipeBodyRef.current) swipeBodyRef.current.style.transform = `translateX(${capped}px)`;
        if (swipeIconRef.current) swipeIconRef.current.style.opacity = String(Math.min(1, Math.abs(capped) / 56));
        return;
      }
    }
    longPress.onTouchMove(e);
  };
  const settleSwipe = () => {
    const s = swipeRef.current;
    const triggered = s.horiz && Math.abs(s.dx) >= 56;
    if (swipeBodyRef.current) {
      swipeBodyRef.current.style.transition = "transform 180ms ease";
      swipeBodyRef.current.style.transform = "translateX(0)";
      const el = swipeBodyRef.current;
      setTimeout(() => { if (el) el.style.transition = ""; }, 220);
    }
    if (swipeIconRef.current) swipeIconRef.current.style.opacity = "0";
    const wasHoriz = s.horiz;
    s.horiz = false;
    s.dx = 0;
    return { triggered, wasHoriz };
  };
  const onSwipeTouchEnd = (e) => {
    const { triggered, wasHoriz } = settleSwipe();
    if (wasHoriz) {
      longPress.onTouchEnd(e);
      if (triggered && onReply && !selectMode) {
        try { if (navigator.vibrate) navigator.vibrate(10); } catch (err) { /* ignore */ }
        onReply(message);
      }
      return; // a swipe never counts as a tap/double-tap
    }
    handleBubbleTouchEnd(e);
  };
  const onSwipeTouchCancel = (e) => {
    settleSwipe();
    longPress.onTouchCancel(e);
  };

  // ---- Deleted tombstone: "(mensagem apagada por FULANO em DATA às HORA)" ----
  if (message.type === "deleted" || message.meta?.deleted) {
    const dm = message.meta || {};
    let dateStr = "";
    let timeStr = "";
    try {
      const dt = new Date(dm.deleted_at || message.created_at);
      dateStr = dt.toLocaleDateString(lang === "pt" ? "pt-BR" : "en-US");
      timeStr = dt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    } catch (e) { /* ignore */ }
    const label = t("deleted_message_by")
      .replace("{name}", dm.deleted_by_name || "")
      .replace("{date}", dateStr)
      .replace("{time}", timeStr);
    return (
      <div className={`flex ${isOwn ? "justify-end" : "justify-start"} my-1`} data-testid="message-deleted">
        <div className="rounded-2xl px-3 py-2 border border-dashed border-muted-foreground/30 bg-muted/40 text-muted-foreground text-xs italic flex items-center gap-1.5 max-w-[85%]">
          <Ban className="h-3.5 w-3.5 shrink-0" />
          <span>{label}</span>
        </div>
      </div>
    );
  }

  // ---- Automatic translation (viewer's language) ----
  // same language on both sides → show the original, never the cached translation
  const tr = (sender?.language && sender.language === lang) ? null : message.translations?.[lang];
  const trContent = (tr?.content || "").trim();
  const contentDiffers = !!trContent && trContent !== (message.content || "").trim();
  const trTranscript = (tr?.transcript || "").trim();
  const transcriptDiffers = !!trTranscript && trTranscript !== ((message.meta?.transcript || "").trim());
  const isTranslated = contentDiffers || transcriptDiffers;
  const displayContent = contentDiffers && !showOriginal ? trContent : message.content;
  const displayTranscript = transcriptDiffers && !showOriginal ? trTranscript : message.meta?.transcript;

  // ---- Ticks (WhatsApp-style): ✓ sent · ✓✓ gray delivered to device · ✓✓ blue read ----
  const otherIds = (conversation?.participant_ids || []).filter((p) => p !== user.id);
  const readByAll = isOwn && otherIds.length > 0 && otherIds.every((p) => reads?.[p] && reads[p] >= message.created_at);
  const deliveredByAll = isOwn && otherIds.length > 0 && otherIds.every(
    (p) => (delivers?.[p] && delivers[p] >= message.created_at) || (reads?.[p] && reads[p] >= message.created_at)
  );
  const reactions = message.reactions || {};

  // ---- In-conversation search highlight (yellow, WhatsApp style) ----
  const highlightText = (textValue, keyPrefix = "hl") => {
    const q = (highlightQuery || "").trim();
    if (!q || !textValue) return textValue;
    const esc = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const parts = String(textValue).split(new RegExp(`(${esc})`, "ig"));
    if (parts.length === 1) return textValue;
    return parts.map((p, i) =>
      p.toLowerCase() === q.toLowerCase() ? (
        <mark key={`${keyPrefix}-${i}`} data-testid="search-highlight" className="bg-yellow-300 dark:bg-yellow-500/70 text-slate-900 dark:text-slate-950 rounded-sm px-0.5">
          {p}
        </mark>
      ) : (
        <React.Fragment key={`${keyPrefix}-${i}`}>{p}</React.Fragment>
      ));
  };

  // ---- @Mentions ----
  const mentionIds = message.meta?.mentions || [];
  const mentionsMe = mentionIds.includes(user.id);
  const mentionNames = (conversation?.participants || [])
    .filter((p) => mentionIds.includes(p.id))
    .map((p) => p.name)
    .sort((a, b) => b.length - a.length);
  const renderWithMentions = (textValue) => {
    if (!textValue || mentionNames.length === 0) return highlightText(textValue);
    const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const rx = new RegExp(`(@(?:${mentionNames.map(esc).join("|")}))`, "g");
    const parts = String(textValue).split(rx);
    return parts.map((part, i) =>
      part.startsWith("@") && mentionNames.includes(part.slice(1)) ? (
        <span key={i} className="font-semibold text-[hsl(var(--primary))] bg-[hsl(var(--accent))/0.18] rounded px-0.5" data-testid="mention-token">
          {part}
        </span>
      ) : (
        <React.Fragment key={i}>{highlightText(part, `m${i}`)}</React.Fragment>
      ));
  };

  if (message.type === "system") {
    // System logs are shown in the viewer's language when a cached translation exists
    const sysTr = (message.translations?.[lang]?.content || "").trim();
    return (
      <div className="flex justify-center my-2 msg-in" data-testid="message-system">
        <span className="text-[11px] text-muted-foreground bg-muted rounded-full px-3 py-1 text-center max-w-[85%]">
          {sysTr || message.content}
        </span>
      </div>
    );
  }

  const structured = ["issue", "measurement", "material_request", "question", "task"].includes(message.type);
  const borderMap = {
    issue: "border-l-[hsl(var(--issue-open))]",
    measurement: "border-l-[hsl(var(--info))]",
    material_request: "border-l-[hsl(var(--warning))]",
    question: "border-l-[hsl(var(--primary))]",
    task: "border-l-[hsl(var(--accent))]",
  };

  const isSticker = message.type === "sticker";
  const stickerable = !message.is_deleted && (message.type === "photo" || message.type === "video") && message.media?.[0]
    ? message.media[0] : null;
  // measurement catalog: photos in media_ids order so photo i belongs to opening i
  const isCatalog = message.type === "measurement" && !!message.meta?.catalog && Array.isArray(message.meta?.items);
  // Edit: own text/media within 15 min, own measurements (single or catalog with source photos) within 2 h
  const editAction = onEdit && isOwn && !message.is_deleted
    && (
      (["text", "photo", "video", "document"].includes(message.type)
        && (Date.now() - new Date(message.created_at).getTime()) < 15 * 60 * 1000)
      || (message.type === "measurement"
        && (message.meta?.src?.media_id || (message.meta?.catalog && (message.meta?.items || []).some((it) => it.src?.media_id)))
        && (Date.now() - new Date(message.created_at).getTime()) < 2 * 60 * 60 * 1000)
    )
    ? () => onEdit(message)
    : null;
  const isMeasurement = message.type === "measurement";
  const byId = (id) => (message.media || []).find((m) => m.id === id && m.kind === "image");
  const measureImages = isMeasurement ? (message.media || []).filter((m) => m.kind === "image") : [];
  const measureOrdered = isMeasurement ? (message.media_ids || []).map(byId).filter(Boolean) : [];
  const measureAll = measureOrdered.length ? measureOrdered : measureImages;
  // extra shots (other angles / rot) live in meta.extra_media_ids (single) or items[i].extra_media_ids (catalog)
  const extraIdSet = new Set(isCatalog
    ? (message.meta.items || []).flatMap((it) => it.extra_media_ids || [])
    : (message.meta?.extra_media_ids || []));
  const catalogPhotos = measureAll.filter((m) => !extraIdSet.has(m.id)); // annotated photo per opening, in order
  const extrasFor = (ids) => (ids || []).map(byId).filter(Boolean);
  const canAddExtra = isMeasurement && isOwn && !message.is_deleted && !selectMode;
  const bubbleBase = isSticker
    ? "!bg-transparent !border-0 !px-0 !py-0 !shadow-none"
    : isOwn
      ? "bg-[hsl(var(--primary)/0.10)] border border-[hsl(var(--primary)/0.18)]"
      : "bg-card border";

  const decisionCls = message.is_decision ? "border-l-4 border-l-[hsl(var(--success))]" : "";
  const structCls = structured ? `border-l-4 ${borderMap[message.type]}` : "";
  const mentionCls = mentionsMe ? "ring-1 ring-[hsl(var(--accent))] shadow-[0_0_0_3px_hsl(var(--accent)/0.12)]" : "";
  const linkedShared = message.meta?.link_visibility === "shared";

  const bubble = (
    <div className={`rounded-2xl px-3 py-2.5 relative ${bubbleBase} ${structCls} ${decisionCls} ${mentionCls} ${message.meta?.urgent ? "border-l-4 border-l-destructive" : ""} ${selectMode && selected ? "ring-2 ring-primary" : ""}`}>
      {/* Resolved issue: diagonal ribbon across the WHOLE card */}
      {message.type === "issue" && message.meta?.status === "resolved" && (
        <div
          data-testid="issue-resolved-ribbon"
          className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center overflow-hidden rounded-2xl"
        >
          <div className="w-[160%] -rotate-[18deg] bg-[hsl(var(--success))]/85 text-white text-center text-xs sm:text-sm font-extrabold uppercase tracking-[0.3em] py-1 shadow-md">
            {t("resolved")}
          </div>
        </div>
      )}
      {/* 🚨 Urgent label */}
      {message.meta?.urgent && (
        <div className="flex items-center gap-1 text-[10px] font-bold text-destructive mb-1" data-testid="urgent-label">
          <AlertTriangle className="h-3 w-3" /> 🚨 {t("urgent_badge")}
        </div>
      )}
      {/* Forwarded chip */}
      {message.meta?.forwarded && (
        <div className="flex items-center gap-1 text-[10px] italic text-muted-foreground mb-1" data-testid="forwarded-chip">
          <Forward className="h-3 w-3" /> {t("forwarded")}
          {message.meta?.forwarded_from ? ` · ${message.meta.forwarded_from}` : ""}
        </div>
      )}
      {/* Mentioned-you chip */}
      {mentionsMe && (
        <div className="flex items-center gap-1 text-[10px] font-bold text-[hsl(var(--accent))] mb-1" data-testid="mentioned-you-chip">
          <AtSign className="h-3 w-3" /> {t("you_were_mentioned")}
        </div>
      )}
      {/* Decision label */}
      {message.is_decision && (
        <div className="flex items-center gap-1 text-[10px] font-bold text-[hsl(var(--success))] mb-1" data-testid="decision-label">
          <Gavel className="h-3 w-3" /> {t("decision")}
        </div>
      )}

      {/* Reply-to: quoted message (click = jump to original) or highlighted photo area */}
      {message.reply_to && message.reply_to.message_id ? (
        <button
          data-testid="reply-quote"
          onClick={(e) => {
            e.stopPropagation();
            if (!selectMode && onQuoteJump) onQuoteJump(message.reply_to.message_id);
          }}
          className="w-full text-left flex items-center gap-2 bg-muted/70 hover:bg-muted rounded-lg p-1.5 mb-2 border-l-2 border-primary transition-colors cursor-pointer"
        >
          {message.reply_to.crop_media_id || (message.reply_to.media_id && (!message.reply_to.type || message.reply_to.type === "photo")) ? (
            <img
              src={mediaSrcById(message.reply_to.crop_media_id || message.reply_to.media_id)}
              alt=""
              data-testid="reply-quote-thumb"
              onClick={async (e) => {
                // Tapping the THUMBNAIL opens the image full-screen (zoomable);
                // tapping the rest of the quote still jumps to the original message.
                e.stopPropagation();
                if (selectMode) return;
                const mid = message.reply_to.crop_media_id || message.reply_to.media_id;
                try {
                  const res = await api.get(`/media/${mid}/info`);
                  onOpenPhoto && onOpenPhoto(res.data, message);
                } catch (err) { /* ignore */ }
              }}
              className="h-12 w-12 object-cover rounded shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
            />
          ) : (
            <span className="relative shrink-0" data-testid="reply-quote-avatar">
              <SenderAvatar
                sender={(conversation?.participants || []).find((p) => p.id === message.reply_to.sender_id) || { name: message.reply_to.sender_name }}
                className="h-10 w-10"
              />
              {message.reply_to.media_id && (
                <span className="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full bg-primary text-primary-foreground flex items-center justify-center ring-2 ring-card" data-testid="reply-quote-icon">
                  {message.reply_to.type === "voice" ? <Mic className="h-2.5 w-2.5" /> : message.reply_to.type === "video" ? <Play className="h-2.5 w-2.5" /> : <FileText className="h-2.5 w-2.5" />}
                </span>
              )}
            </span>
          )}
          <span className="min-w-0 flex-1">
            <span className="block text-[12px] font-semibold text-primary truncate">{message.reply_to.sender_name || t("reply")}</span>
            <span className="block text-[12px] text-muted-foreground truncate">
              {message.reply_to.crop_media_id
                ? (message.reply_to.content || t("area_highlighted"))
                : (message.reply_to.content || (message.reply_to.type === "photo" ? `\u{1F4F8} ${t("photo")}` : message.reply_to.type === "voice" ? `\u{1F399} ${t("voice_message")}` : ""))}
            </span>
          </span>
          <Reply className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        </button>
      ) : message.reply_to ? (
        <div className="flex items-center gap-2 bg-muted/70 rounded-lg p-1.5 mb-2 border-l-2 border-primary" data-testid="reply-quote">
          {message.reply_to.crop_media_id && (
            <img
              src={mediaSrcById(message.reply_to.crop_media_id)}
              alt=""
              data-testid="reply-crop-image"
              onClick={async (e) => {
                e.stopPropagation();
                if (selectMode) return;
                try {
                  const res = await api.get(`/media/${message.reply_to.crop_media_id}/info`);
                  onOpenPhoto && onOpenPhoto(res.data, message);
                } catch (err) { /* ignore */ }
              }}
              className="h-12 w-12 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
            />
          )}
          <div className="text-[10px] text-muted-foreground flex items-center gap-1">
            <MapPin className="h-3 w-3" /> {t("area_highlighted")}
          </div>
        </div>
      ) : null}

      {/* Linked job chip */}
      {message.meta?.linked_job && (
        <button
          data-testid="linked-job-chip"
          onClick={(e) => { e.stopPropagation(); if (!selectMode) navigate(`/jobs/${message.meta.linked_job.id}`); }}
          className="flex items-center gap-1.5 bg-primary/10 hover:bg-primary/20 border border-primary/25 rounded-full px-2.5 py-1 mb-1.5 transition-colors"
        >
          <Briefcase className="h-3 w-3 text-primary" />
          <span className="text-[11px] font-mono-plex font-semibold text-primary">#{message.meta.linked_job.job_number}</span>
          <span className="text-[11px] text-muted-foreground truncate max-w-[130px]">{message.meta.linked_job.customer_name}</span>
          {linkedShared
            ? <Megaphone data-testid="link-shared-icon" className="h-3 w-3 text-[hsl(var(--success))]" />
            : <Lock data-testid="link-private-icon" className="h-3 w-3 text-muted-foreground" />}
        </button>
      )}

      {/* Structured headers */}
      {message.type === "issue" && (
        <div className="mb-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            <AlertTriangle className="h-4 w-4 text-[hsl(var(--issue-open))]" />
            <span className="font-bold text-xs uppercase tracking-wide text-[hsl(var(--issue-open))]">{t("issue")}</span>
            {message.meta?.reopened && (
              <Badge data-testid="issue-reopened-badge" className="bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/40 text-[9px] px-1.5 py-0 flex items-center gap-0.5">
                <RotateCcw className="h-2.5 w-2.5" /> {t("reopened")}
              </Badge>
            )}
            <PriorityBadge priority={message.meta?.priority} t={t} />
            <StatusChip
              status={message.meta?.status || "open"}
              t={t}
              interactive={canManageIssues && !!message.meta?.issue_id && !selectMode}
              onSelect={(s) => onIssueStatus && onIssueStatus(message.meta.issue_id, s)}
            />
          </div>
          <div className="font-semibold msg-text-sub mt-1">{highlightText(message.meta?.title, "ttl")}</div>
          {message.meta?.location && (
            <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
              <MapPin className="h-3 w-3" /> {message.meta.location}
            </div>
          )}
          {message.meta?.issue_id && message.meta?.status !== "resolved" && !selectMode && onResolveIssue && (
            <button
              data-testid="resolve-issue-button"
              onClick={(e) => { e.stopPropagation(); onResolveIssue(message); }}
              className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--success))]/50 text-[hsl(var(--success))] px-3 py-1 text-xs font-semibold hover:bg-[hsl(var(--success))]/10 transition-colors"
            >
              <CheckCircle2 className="h-3.5 w-3.5" /> {t("resolve_issue")}
            </button>
          )}
          {/* Supervisor can reopen a resolved issue right from the conversation */}
          {message.meta?.issue_id && message.meta?.status === "resolved" && !selectMode && canManageIssues && onIssueStatus && (
            <button
              data-testid="reopen-issue-button"
              onClick={(e) => { e.stopPropagation(); onIssueStatus(message.meta.issue_id, "open"); }}
              className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-amber-500/50 text-amber-600 dark:text-amber-400 px-3 py-1 text-xs font-semibold hover:bg-amber-500/10 transition-colors"
            >
              <RotateCcw className="h-3.5 w-3.5" /> {t("reopen_issue")}
            </button>
          )}
        </div>
      )}

      {message.type === "measurement" && (
        <div className="mb-1">
          <div className="flex items-center gap-2">
            <Ruler className="h-4 w-4 text-primary" />
            <span className="font-bold text-xs uppercase tracking-wide text-primary">
              {message.meta?.catalog ? t("measure_catalog") : t("measurement")}
            </span>
            {message.meta?.match === false && (
              <span className="text-[10px] font-bold uppercase rounded-full px-2 py-0.5 bg-destructive/10 text-destructive" data-testid="measure-badge-wrong">
                {t("measure_mismatch")}
              </span>
            )}
            {message.meta?.match === true && (
              <span className="text-[10px] font-bold uppercase rounded-full px-2 py-0.5 bg-emerald-500/10 text-emerald-600" data-testid="measure-badge-ok">
                {t("measure_match_short")}
              </span>
            )}
          </div>
          {message.meta?.location && (
            <div className="text-xs text-muted-foreground mt-1">
              {message.meta.location}
              {message.meta?.subtype ? ` · ${message.meta.subtype}` : ""}
            </div>
          )}
          {message.meta?.catalog && Array.isArray(message.meta?.items) ? (
            <div className="mt-1.5 space-y-1.5" data-testid="measure-catalog-items">
              {message.meta.items.map((it, i) => (
                <div key={i} className="rounded-lg border bg-background/50 px-2.5 py-1.5" data-testid="measure-catalog-item">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-semibold truncate">{it.location || `${i + 1}.`}</span>
                    {it.match === false && (
                      <span className="ml-auto text-[9px] font-bold uppercase rounded-full px-1.5 py-0.5 bg-destructive/10 text-destructive shrink-0">
                        {t("measure_mismatch")}
                      </span>
                    )}
                    {it.match === true && (
                      <span className="ml-auto text-[9px] font-bold uppercase rounded-full px-1.5 py-0.5 bg-emerald-500/10 text-emerald-600 shrink-0">
                        {t("measure_match_short")}
                      </span>
                    )}
                  </div>
                  {Array.isArray(it.units) && it.units.length > 0 ? (
                    <div className="mt-1 space-y-0.5">
                      {it.units.map((u, k) => (
                        <div key={k} data-testid="measure-unit-row">
                          <div className="flex items-baseline gap-1.5 text-[11px]">
                            <span className="font-semibold shrink-0">{k + 1}.</span>
                            <span className="truncate">{[u.location || u.label || "", u.subtype].filter(Boolean).join(" · ")}</span>
                            <span className="font-mono-plex font-semibold shrink-0">{u.value}</span>
                            {u.expected && (
                              <span className={`font-mono-plex shrink-0 ${u.match === false ? "text-destructive font-semibold" : "text-muted-foreground"}`}>
                                PDF {u.expected}{u.match === false ? " ✗" : u.match === true ? " ✓" : ""}
                              </span>
                            )}
                          </div>
                          {u.note && <div className="text-[10px] text-muted-foreground italic pl-4">📝 {u.note}</div>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="font-mono-plex text-xs font-semibold mt-0.5">{it.value}</div>
                  )}
                  {it.overall && (
                    <div className="text-[11px] font-mono-plex text-sky-600 dark:text-sky-400 mt-0.5" data-testid="measure-overall-line">
                      Opening: {it.overall}
                    </div>
                  )}
                  {!it.units && it.expected && (
                    <div className={`font-mono-plex text-[11px] ${it.match === false ? "text-destructive font-semibold" : "text-muted-foreground"}`}>
                      PDF: {it.expected}
                    </div>
                  )}
                  {it.note && <div className="text-[11px] text-muted-foreground mt-0.5">{it.note}</div>}
                  {/* the annotated photo of THIS opening sits right under its numbers (+ extra shots beside it) */}
                  {(catalogPhotos[i] || canAddExtra) && (
                    <div data-testid="measure-catalog-photo">
                      <MeasurePhotos main={catalogPhotos[i]} extras={extrasFor(it.extra_media_ids)} message={message} index={i}
                                     canAdd={canAddExtra} onOpenPhoto={onOpenPhoto} selectMode={selectMode} lang={lang} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : Array.isArray(message.meta?.units) && message.meta.units.length > 0 ? (
            <div className="mt-1 space-y-1" data-testid="measure-units-list">
              {message.meta.units.map((u, k) => (
                <div key={k} className="rounded-lg border bg-background/50 px-2.5 py-1.5" data-testid="measure-unit-row">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-semibold truncate">
                      {k + 1}. {[u.location || u.label || (u.kind === "door" ? t("measure_door") : t("measure_window")), u.subtype].filter(Boolean).join(" · ")}
                    </span>
                    {u.match === false && (
                      <span className="ml-auto text-[9px] font-bold uppercase rounded-full px-1.5 py-0.5 bg-destructive/10 text-destructive shrink-0">
                        {t("measure_mismatch")}
                      </span>
                    )}
                    {u.match === true && (
                      <span className="ml-auto text-[9px] font-bold uppercase rounded-full px-1.5 py-0.5 bg-emerald-500/10 text-emerald-600 shrink-0">
                        {t("measure_match_short")}
                      </span>
                    )}
                  </div>
                  <div className="font-mono-plex text-xs font-semibold mt-0.5">{u.value}</div>
                  {u.expected && (
                    <div className={`font-mono-plex text-[11px] ${u.match === false ? "text-destructive font-semibold" : "text-muted-foreground"}`}>
                      PDF: {u.expected}
                    </div>
                  )}
                  {u.note && <div className="text-[11px] text-muted-foreground mt-0.5">{u.note}</div>}
                </div>
              ))}
              {message.meta?.overall && (
                <div className="text-xs font-mono-plex font-semibold text-sky-600 dark:text-sky-400" data-testid="measure-overall-line">
                  Opening: {message.meta.overall}
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="font-mono-plex font-semibold text-base mt-0.5">{message.meta?.value}</div>
              {message.meta?.expected && (
                <div className={`text-xs mt-0.5 font-mono-plex ${message.meta?.match === false ? "text-destructive font-semibold" : "text-muted-foreground"}`}>
                  PDF: {message.meta.expected}
                </div>
              )}
              {message.meta?.overall && (
                <div className="text-xs font-mono-plex font-semibold text-sky-600 dark:text-sky-400" data-testid="measure-overall-line">
                  Opening: {message.meta.overall}
                </div>
              )}
              {message.meta?.note && <div className="text-xs text-muted-foreground mt-0.5">{message.meta.note}</div>}
            </>
          )}
          {!isCatalog && (catalogPhotos[0] || canAddExtra) && (
            <MeasurePhotos main={catalogPhotos[0]} extras={extrasFor(message.meta?.extra_media_ids)} message={message}
                           canAdd={canAddExtra} onOpenPhoto={onOpenPhoto} selectMode={selectMode} lang={lang} />
          )}
        </div>
      )}

      {message.type === "material_request" && (
        <div className="mb-1">
          <div className="flex items-center gap-2">
            <Package className="h-4 w-4 text-accent-foreground" />
            <span className="font-bold text-xs uppercase tracking-wide text-accent-foreground">{t("material_request")}</span>
          </div>
          <div className="msg-text-sub font-medium mt-1">{contentDiffers && !showOriginal ? trContent : message.meta?.items}</div>
          {message.meta?.needed_by && (
            <div className="text-xs text-muted-foreground mt-0.5">{t("needed_by")}: {message.meta.needed_by}</div>
          )}
        </div>
      )}

      {message.type === "trim" && <TrimReportBubble message={message} />}

      {message.type === "question" && (
        <div className="flex items-center gap-2 mb-1">
          <HelpCircle className="h-4 w-4 text-primary" />
          <span className="font-bold text-xs uppercase tracking-wide text-primary">{t("question")}</span>
        </div>
      )}

      {/* Poll — interactive voting (question + options rendered inside) */}
      {message.type === "poll" && (
        <PollBubble message={message} conversation={conversation} />
      )}

      {message.type === "task" && (
        <div className="mb-1">
          <div className="flex items-center gap-2">
            <CheckSquare className="h-4 w-4 text-accent-foreground" />
            <span className="font-bold text-xs uppercase tracking-wide text-accent-foreground">{t("task")}</span>
            {message.meta?.status === "done" && <Badge className="bg-[hsl(var(--success))] text-white text-[9px]">{t("done")}</Badge>}
          </div>
          {message.meta?.assigned_to_name && (
            <div className="text-xs text-muted-foreground mt-0.5">
              {t("assigned_to")}: <span className="font-medium text-foreground">{message.meta.assigned_to_name}</span>
              {message.meta?.due_label ? ` \u00b7 ${t("due")}: ${message.meta.due_label}` : ""}
            </div>
          )}
        </div>
      )}

      {/* Sticker (WhatsApp-style: no bubble, big transparent image) */}
      {isSticker && message.media?.[0] && (
        <LocalizedSticker media={message.media[0]} className="h-40 w-40 object-contain select-none drop-shadow-md" />
      )}
      {/* Photos */}
      {!isMeasurement && message.media?.filter((m) => m.kind === "image").length > 0 && (
        <div className={`grid gap-1.5 ${message.media.filter((m) => m.kind === "image").length > 1 ? "grid-cols-2" : "grid-cols-1"} mb-1.5 mt-1`}>
          {message.media.filter((m) => m.kind === "image").map((m) => (
            <div key={m.id} className="relative cursor-pointer" data-testid="message-photo" onClick={(e) => { if (!selectMode) { e.stopPropagation(); onOpenPhoto && onOpenPhoto(m, message); } }}>
              <div className="relative">
                <img src={mediaSrc(m)} alt="" loading="lazy" className="rounded-lg w-full max-h-72 object-cover border" />
                <TextOverlays media={m} lang={lang} size="sm" />
              </div>
              {m.category && (
                <Badge className="absolute top-1.5 left-1.5 bg-black/60 text-white text-[9px] border-0">
                  {t(m.category === "issue" ? "issue_cat" : m.category)}
                </Badge>
              )}
              {m.version > 1 && (
                <Badge className="absolute top-1.5 right-1.5 bg-accent text-accent-foreground text-[9px] border-0">
                  {m.version_label || `v${m.version}`}
                </Badge>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Videos — click opens the fullscreen player (with pause + markup) */}
      {message.media?.filter((m) => m.kind === "video").map((m) => (
        <div
          key={m.id}
          data-testid="message-video"
          onClick={(e) => {
            if (selectMode) return;
            e.stopPropagation();
            onOpenVideo && onOpenVideo(m, message);
          }}
          className="relative cursor-pointer mb-1.5 mt-1"
        >
          <video
            src={mediaSrc(m) + (m.poster ? "" : "#t=0.1")}
            poster={m.poster || undefined}
            preload="metadata"
            muted
            playsInline
            className="rounded-lg w-full max-h-72 border bg-black pointer-events-none"
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="h-12 w-12 rounded-full bg-black/60 flex items-center justify-center hover:bg-black/75 transition-colors">
              <Play className="h-6 w-6 text-white ml-0.5" />
            </div>
          </div>
        </div>
      ))}

      {/* Buddy text chat: draft awaiting the user's tap (Confirm / Cancel) */}
      {message.meta?.buddy_draft && (
        <div className="mt-2 rounded-lg border border-amber-400/60 bg-amber-50 dark:bg-amber-950/30 px-2.5 py-2 text-xs space-y-1.5" data-testid="chat-buddy-draft">
          <div className="font-semibold text-amber-700 dark:text-amber-300 uppercase tracking-wide text-[10px]">
            {chatDraftLabel(message.meta.buddy_draft, t)}
          </div>
          {(message.meta.buddy_draft.text || message.meta.buddy_draft.title || message.meta.buddy_draft.fact) && (
            <div className="text-[13px] text-foreground">{message.meta.buddy_draft.text || message.meta.buddy_draft.title || message.meta.buddy_draft.fact}</div>
          )}
          {(chatDraftStatus || message.meta.buddy_draft.status) === "pending" ? (
            <div className="flex gap-2 pt-0.5">
              <Button size="sm" variant="outline" className="h-8 flex-1" data-testid="chat-buddy-draft-cancel" disabled={chatDraftBusy}
                onClick={(e) => { e.stopPropagation(); resolveChatDraft("cancel"); }}>{t("cancel")}</Button>
              <Button size="sm" className="h-8 flex-1" data-testid="chat-buddy-draft-confirm" disabled={chatDraftBusy}
                onClick={(e) => { e.stopPropagation(); resolveChatDraft("confirm"); }}>{chatDraftBusy ? "..." : t("buddy_confirm")}</Button>
            </div>
          ) : (
            <div className="text-[11px] text-muted-foreground">{(chatDraftStatus || message.meta.buddy_draft.status) === "done" ? "✅" : "✕"} {(chatDraftStatus || message.meta.buddy_draft.status) === "done" ? t("buddy_confirm") : t("cancel")}</div>
          )}
        </div>
      )}

      {/* Voice */}
      {message.type === "voice" && message.media?.[0] && (
        <div data-testid="voice-note-player" className="min-w-[220px]">
          <div className="flex items-center gap-1.5" data-voice-wrap>
            <Mic
              data-testid={isOwn && Object.keys(message.played_by || {}).length ? "voice-mic-played" : "voice-mic"}
              className={`h-4 w-4 shrink-0 ${isOwn && Object.keys(message.played_by || {}).length ? "text-sky-500" : "text-primary"}`}
            />
            <VoiceNotePlayer media={message.media[0]} durationHint={message.meta?.duration} messageId={message.id} convId={message.conversation_id} senderName={sender?.name} createdAt={message.created_at} />
            <button
              type="button"
              data-testid="voice-speed-btn"
              onClick={(e) => {
                e.stopPropagation();
                const next = voiceSpeed === 1 ? 1.5 : voiceSpeed === 1.5 ? 2 : 1;
                setVoiceSpeed(next);
                try { localStorage.setItem("osc_voice_speed", String(next)); } catch (err) { /* ignore */ }
                setVoiceRate(next);
              }}
              className="shrink-0 h-9 min-w-[46px] px-2 rounded-full border bg-card text-xs font-bold tabular-nums text-primary hover:bg-muted active:scale-95 transition-transform"
              title="1x / 1.5x / 2x"
            >
              {voiceSpeed}x
            </button>
          </div>
          {message.meta?.transcript && (
            <p data-testid="voice-note-transcript" className="text-[14px] sm:text-xs text-muted-foreground italic mt-1.5 border-l-2 border-muted pl-2">
              “{highlightText(displayTranscript, "tr")}”
            </p>
          )}
        </div>
      )}

      {/* Document */}
      {message.type === "document" && message.media?.[0] && (
        (message.media[0].content_type === "application/pdf" || /\.pdf$/i.test(message.media[0].filename || "")) ? (
          <>
            <PdfThumbnail
              media={message.media[0]}
              width={170}
              maxHeight={185}
              onClick={(e) => { if (e) e.stopPropagation(); if (!selectMode) setPdfOpen(true); }}
            />
            <PdfViewer open={pdfOpen} media={message.media[0]} onClose={() => setPdfOpen(false)} />
          </>
        ) : (
          <div className="flex items-center gap-2" data-testid="message-document">
            <a href={mediaSrc(message.media[0])} target="_blank" rel="noreferrer" className="flex items-center gap-2 msg-text-sub text-primary underline min-w-0">
              <FileText className="h-4 w-4 shrink-0" /> <span className="truncate">{highlightText(message.media[0].filename || "Document", "doc")}</span>
            </a>
            <button type="button" data-testid="message-document-share" aria-label={t("share")}
              className="h-7 w-7 rounded-md hover:bg-black/5 flex items-center justify-center shrink-0 text-primary"
              onClick={async (e) => { e.stopPropagation(); const ok = await shareNative({ url: mediaSrc(message.media[0]), filename: message.media[0].filename, title: message.media[0].filename }); if (!ok) toast.info(t("share_unavailable")); }}>
              <Share2 className="h-4 w-4" />
            </button>
          </div>
        )
      )}

      {/* Text content */}
      {message.content && message.type !== "voice" && message.type !== "issue" && message.type !== "measurement" && message.type !== "trim" && message.type !== "material_request" && message.type !== "poll" && (
        <p className="msg-text whitespace-pre-wrap break-words">{renderWithMentions(displayContent)}</p>
      )}
      {message.type === "issue" && message.content && message.content !== message.meta?.title && (
        <p className="msg-text whitespace-pre-wrap break-words">{renderWithMentions(displayContent)}</p>
      )}

      {/* Translation indicator + toggle */}
      {isTranslated && (
        <button
          data-testid="translation-toggle"
          onClick={(e) => { e.stopPropagation(); setShowOriginal((s) => !s); }}
          className="flex items-center gap-1 text-[10px] text-primary/80 hover:text-primary mt-1 transition-colors"
        >
          <Languages className="h-3 w-3" />
          {showOriginal ? t("show_translation") : `${t("translated")} · ${t("see_original")}`}
        </button>
      )}

      {/* Reactions */}
      {Object.keys(reactions).length > 0 && (
        <div className="flex gap-1 mt-1.5 flex-wrap" data-testid="reactions-row">
          {Object.entries(reactions).map(([emoji, uids]) => (
            <button
              key={emoji}
              data-testid="reaction-chip"
              onClick={(e) => { e.stopPropagation(); if (!selectMode) setReactionsOpen(true); }}
              className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs transition-colors ${
                uids.includes(user.id) ? "bg-primary/15 border-primary/40" : "bg-muted/60 hover:bg-muted"
              }`}
            >
              <span>{emoji}</span>
              <span className="text-[10px] font-semibold text-muted-foreground">{uids.length}</span>
            </button>
          ))}
        </div>
      )}

      {/* Mention read receipts: the author sees when each mentioned person viewed this message */}
      {isOwn && mentionIds.length > 0 && (
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1" data-testid="mention-receipts">
          {(conversation?.participants || []).filter((p) => mentionIds.includes(p.id)).map((p) => {
            const seen = !!(reads?.[p.id] && reads[p.id] >= message.created_at);
            return (
              <span
                key={p.id}
                data-testid={seen ? "mention-seen" : "mention-unseen"}
                className={`flex items-center gap-1 text-[10px] ${seen ? "text-[hsl(var(--success))]" : "text-muted-foreground/70"}`}
              >
                {seen ? <CheckCheck className="h-3 w-3" /> : <Check className="h-3 w-3" />}
                {seen ? `${t("seen_by")} ${p.name.split(" ")[0]}` : `${p.name.split(" ")[0]} ${t("not_seen_yet")}`}
              </span>
            );
          })}
        </div>
      )}

      {/* Mandatory "Entendi" acknowledgment (broadcast notices) */}
      {message.meta?.ack_required && (
        isOwn ? (
          <button
            type="button"
            data-testid="ack-status-chip"
            onClick={(e) => { e.stopPropagation(); if (!selectMode && message.meta?.broadcast_id) setAcksOpen(true); }}
            className={`mt-1.5 w-full flex items-center justify-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[11px] font-medium transition-colors ${
              message.meta?.ack
                ? "border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]"
                : "border-muted-foreground/25 bg-muted/40 text-muted-foreground hover:bg-muted"
            }`}
          >
            {message.meta?.ack ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Megaphone className="h-3.5 w-3.5" />}
            {message.meta?.ack
              ? `${t("ack_confirmed")} · ${fmtTime(message.meta.ack.at)}`
              : t("ack_waiting")}
            {message.meta?.broadcast_id && <span className="underline underline-offset-2 ml-0.5">{t("ack_see_confirmations")}</span>}
          </button>
        ) : (message.meta?.ack || ackLocal) ? (
          <div
            data-testid="ack-done-chip"
            className="mt-1.5 flex items-center justify-center gap-1.5 rounded-lg bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] px-2.5 py-1.5 text-[11px] font-semibold"
          >
            <CheckCircle2 className="h-3.5 w-3.5" /> {t("ack_button")} · {fmtTime((message.meta?.ack || ackLocal).at)}
          </div>
        ) : (
          <button
            type="button"
            data-testid="ack-button"
            disabled={ackBusy}
            onClick={async (e) => {
              e.stopPropagation();
              if (selectMode || ackBusy) return;
              setAckBusy(true);
              try {
                const res = await api.post(`/messages/${message.id}/ack`, { user_id: user.id });
                setAckLocal(res.data.ack || { user_id: user.id, at: new Date().toISOString() });
              } catch (err) {
                toast.error(t("something_wrong"));
              } finally {
                setAckBusy(false);
              }
            }}
            className="mt-1.5 w-full flex items-center justify-center gap-1.5 rounded-lg bg-primary text-primary-foreground px-3 py-2 text-xs font-bold hover:bg-primary/90 active:scale-[0.99] transition-[background-color,transform] disabled:opacity-60"
          >
            <Check className="h-4 w-4" /> {t("ack_button")}
          </button>
        )
      )}

      {/* 🚨 Urgent: reminders exhausted and still unread — sender-side warning */}
      {isOwn && message.meta?.urgent && message.meta?.escalation_status === "unseen" && !readByAll && (
        <div
          data-testid="urgent-unseen-chip"
          className="mt-1.5 flex items-center justify-center gap-1.5 rounded-lg bg-destructive/10 text-destructive px-2.5 py-1.5 text-[11px] font-semibold"
        >
          <AlertTriangle className="h-3.5 w-3.5" /> {t("urgent_unseen")}
        </div>
      )}

      {/* Footer: time + read receipts + actions */}
      <div className="flex items-center justify-end gap-1 mt-1">
        {message.edited_at && (
          <span className="text-[10px] italic text-muted-foreground" data-testid="edited-label">{t("edited_label")}</span>
        )}
        {message.sender_id === "buddy-bot" && message.type === "text" && (message.content || "").trim() && (
          <BuddySpeakButton text={message.content} userId={user.id} />
        )}
        <span className="text-[11px] text-muted-foreground">{fmtTime(message.created_at)}</span>
        {isOwn && (
          readByAll
            ? <CheckCheck data-testid="tick-read" className="h-3.5 w-3.5 text-primary" />
            : deliveredByAll
              ? <CheckCheck data-testid="tick-delivered" className="h-3.5 w-3.5 text-muted-foreground" />
              : <Check data-testid="tick-sent" className="h-3.5 w-3.5 text-muted-foreground" />
        )}
        {!selectMode && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="message-actions-button" onPointerDown={() => hapticSelect()} className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity h-5 w-5 flex items-center justify-center rounded hover:bg-muted">
                <MoreVertical className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onPointerDownCapture={(e) => { if (e.target.closest("button,[role=menuitem]")) hapticSelect(); }}>
              <div className="flex gap-0.5 px-1.5 py-1" data-testid="quick-reactions-row">
                {QUICK_REACTIONS.map((emoji) => (
                  <button
                    key={emoji}
                    data-testid={`quick-react-${emoji}`}
                    onClick={(e) => {
                      hapticSelect();
                      onReact && onReact(message, emoji);
                      e.currentTarget.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true }));
                    }}
                    className="h-9 w-9 text-lg rounded-lg hover:bg-muted flex items-center justify-center transition-transform hover:scale-110"
                  >
                    {emoji}
                  </button>
                ))}
                <button
                  data-testid="quick-react-more"
                  onClick={(e) => { setEmojiOpen(true); e.currentTarget.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true })); }}
                  className="h-9 w-9 rounded-lg hover:bg-muted flex items-center justify-center text-muted-foreground"
                  aria-label={t("pick_emoji")}
                >
                  <SmilePlus className="h-5 w-5" />
                </button>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="action-reply" onClick={() => onReply && onReply(message)}>
                <Reply className="h-4 w-4 mr-2" /> {t("reply")}
              </DropdownMenuItem>
              {editAction && (
                <DropdownMenuItem data-testid="action-edit" onClick={editAction}>
                  <Pencil className="h-4 w-4 mr-2" /> {t("edit")}
                </DropdownMenuItem>
              )}
              {!message.is_deleted && message.type !== "system" && (
                <DropdownMenuItem data-testid="action-message-info" onClick={() => setInfoOpen(true)}>
                  <Info className="h-4 w-4 mr-2" /> {t("message_info")}
                </DropdownMenuItem>
              )}
              {stickerable && (
                <DropdownMenuItem data-testid="action-make-sticker" onClick={() => setStickerMedia(stickerable)}>
                  <StickerIcon className="h-4 w-4 mr-2" /> {t("make_sticker")}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem data-testid="action-link-to-job" onClick={() => onStartSelect && onStartSelect(message)}>
                <Link2 className="h-4 w-4 mr-2" /> {message.meta?.linked_job ? t("relink_to_job") : t("link_to_job")}
              </DropdownMenuItem>
              {message.meta?.linked_job && onUnlink && (
                <DropdownMenuItem data-testid="action-unlink-job" onClick={() => onUnlink(message)}>
                  <Unlink className="h-4 w-4 mr-2" /> {t("unlink_from_job")} #{message.meta.linked_job.job_number}
                </DropdownMenuItem>
              )}
              {message.type === "document" && (message.media?.[0]?.content_type === "application/pdf" || /\.pdf$/i.test(message.media?.[0]?.filename || "")) && (
                <DropdownMenuItem data-testid="action-save-to-files" onClick={() => onSaveToFiles && onSaveToFiles(message)}>
                  <FileText className="h-4 w-4 mr-2" /> {t("save_to_job_files")}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem data-testid="action-create-issue" onClick={() => onCreateIssue && onCreateIssue(message)}>
                <AlertTriangle className="h-4 w-4 mr-2" /> {t("create_issue")}
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="action-create-task" onClick={() => onCreateTask && onCreateTask(message)}>
                <CheckSquare className="h-4 w-4 mr-2" /> {t("create_task")}
              </DropdownMenuItem>
              {message.meta?.linked_job && !linkedShared && (
                <DropdownMenuItem data-testid="action-share-with-job" onClick={() => onShareWithJob && onShareWithJob(message)}>
                  <Megaphone className="h-4 w-4 mr-2" /> {t("share_with_job")}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem data-testid="mark-decision-button" onClick={() => onToggleDecision && onToggleDecision(message)}>
                <Gavel className="h-4 w-4 mr-2" />
                {message.is_decision ? t("unmark_decision") : t("mark_decision")}
              </DropdownMenuItem>
              {onDelete && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    data-testid="action-delete-message"
                    className="text-destructive focus:text-destructive"
                    onClick={() => onDelete(message)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" /> {t("delete_message")}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );

  // ---- Long-press action sheet handlers ----
  const copyableText = (displayContent || displayTranscript || message.content || "").trim();
  const firstMedia = message.media?.[0] || null;
  // WhatsApp-style: ANYONE can delete any message FOR ME; "for everyone" is
  // limited to own recent messages (48h) or admins.
  const canDeleteMsg = true;
  const canDeleteForAll = isOwn
    ? (user.role === "admin" || (Date.now() - new Date(message.created_at).getTime()) < 48 * 60 * 60 * 1000)
    : user.role === "admin";

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(copyableText);
      toast.success(t("copied"));
    } catch (e) {
      try {
        const ta = document.createElement("textarea");
        ta.value = copyableText;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        toast.success(t("copied"));
      } catch (err) {
        toast.error(t("something_wrong"));
      }
    }
  };

  const saveSticker = async () => {
    try { await api.post("/stickers/mine", { media_id: firstMedia.id }); toast.success(t("sticker_saved")); } catch (e) { toast.error(t("something_wrong")); }
  };

  const saveMedia = async () => {
    const list = (message.media || []).length ? message.media : (firstMedia ? [firstMedia] : []);
    if (!list.length) return;
    const r = await saveToDevice(list.map((m) => ({
      url: mediaSrc(m),
      filename: mediaFilename(m, message.meta?.linked_job || conversation?.job),
      kind: m.kind,
    })));
    if (r === "saved") toast.success(t("saved_to_gallery_n").replace("{n}", list.length));
    else if (r === "downloaded") toast.success(t("saved_to_device"));
    else if (r === false) toast.error(t("something_wrong"));
  };

  const deleteMsg = async () => {
    try {
      await api.delete(`/messages/${message.id}`);
      // The thread removes the bubble via the message_delete websocket event
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      toast.error(detail === "delete_window_expired" ? t("delete_window_expired")
        : e?.response?.status === 403 ? t("delete_message") : t("something_wrong"));
    }
  };

  const hideForMe = async () => {
    try {
      await api.post(`/messages/${message.id}/hide`);
      setHiddenLocally(true); // no websocket: only THIS user stops seeing it
    } catch (e) {
      toast.error(t("something_wrong"));
    }
  };

  if (hiddenLocally) return null; // deleted "for me" in this session

  return (
    <div
      className={`relative flex ${isOwn ? "justify-end" : "justify-start"} my-1 msg-in group ${selectMode ? "cursor-pointer" : ""} select-none sm:select-auto`}
      style={{ WebkitTouchCallout: "none" }}
      data-testid={`message-${message.type}`}
      onClick={() => selectMode && onToggleSelect && onToggleSelect(message)}
      onDoubleClick={quickLike}
      onTouchStart={onSwipeTouchStart}
      onTouchMove={onSwipeTouchMove}
      onTouchEnd={onSwipeTouchEnd}
      onTouchCancel={onSwipeTouchCancel}
      onClickCapture={longPress.onClickCapture}
      onContextMenu={longPress.onContextMenu}
    >
      {/* Swipe-to-reply hint (fades in while dragging) */}
      {!selectMode && onReply && (
        <div
          ref={swipeIconRef}
          className={`absolute top-1/2 -translate-y-1/2 ${isOwn ? "right-0" : "left-0"} opacity-0 pointer-events-none`}
          data-testid="swipe-reply-hint"
        >
          <div className="h-7 w-7 rounded-full bg-muted border flex items-center justify-center">
            <Reply className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>
      )}
      {selectMode && (
        <div className={`self-center mr-2 h-5 w-5 rounded-full border-2 flex items-center justify-center shrink-0 ${selected ? "bg-primary border-primary" : "border-muted-foreground/40"}`} data-testid="select-checkbox">
          {selected && <Check className="h-3 w-3 text-white" />}
        </div>
      )}
      {/* Profile photo next to other people's messages in GROUP chats (WhatsApp style) */}
      {!isOwn && conversation?.type !== "dm" && (
        <div className="mr-1.5 self-start mt-0.5">
          <SenderAvatar sender={sender} />
        </div>
      )}
      <div ref={swipeBodyRef} className={`max-w-[85%] sm:max-w-[62%] min-w-0 ${isOwn ? "items-end" : "items-start"}`}>
        {!isOwn && (
          <div className="text-[12px] font-semibold mb-0.5 px-1" style={{ color: sender?.avatar_color || "inherit" }}>
            {sender?.name}
          </div>
        )}
        {bubble}
      </div>

      {/* Long-press / right-click actions */}
      <MessageActionsSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        canCopy={!!copyableText}
        canSave={!!firstMedia}
        canDelete={canDeleteMsg}
        isStarred={!!isStarred}
        onStar={onToggleStar ? () => onToggleStar(message) : null}
        onPin={onPin ? () => onPin(message) : null}
        onReact={onReact ? (emoji) => onReact(message, emoji) : null}
        onReply={onReply ? () => onReply(message) : null}
        onInfo={!message.is_deleted && message.type !== "system" ? () => setInfoOpen(true) : null}
        onMakeSticker={stickerable ? () => setStickerMedia(stickerable) : null}
        onEdit={editAction}
        onLinkJob={onStartSelect ? () => onStartSelect(message) : null}
        linkLabel={message.meta?.linked_job ? t("relink_to_job") : t("link_to_job")}
        onUnlink={message.meta?.linked_job && onUnlink ? () => onUnlink(message) : null}
        unlinkLabel={message.meta?.linked_job ? `${t("unlink_from_job")} #${message.meta.linked_job.job_number}` : ""}
        onCopy={copyText}
        onSave={isSticker ? saveSticker : saveMedia}
        saveLabel={isSticker ? t("save_sticker") : undefined}
        onForward={() => setForwardOpen(true)}
        onMoreEmoji={onReact ? () => setEmojiOpen(true) : null}
        onDelete={() => setConfirmDelete(true)}
      />
      <ForwardDialog open={forwardOpen} message={message} onClose={() => setForwardOpen(false)} />
      <EmojiPickerDialog open={emojiOpen} onClose={() => setEmojiOpen(false)} onPick={(emoji) => { hapticSelect(); onReact && onReact(message, emoji); }} />
      <ReactionsSheet open={reactionsOpen} onClose={() => setReactionsOpen(false)} reactions={reactions} participants={conversation?.participants} onRemoveMine={(emoji) => onReact && onReact(message, emoji)} />
      <MessageInfoSheet open={infoOpen} onClose={() => setInfoOpen(false)} message={message} />
      {stickerMedia && <MessageStickerStudio media={stickerMedia} onClose={() => setStickerMedia(null)} />}
      {isOwn && message.meta?.broadcast_id && (
        <AckStatusDialog open={acksOpen} broadcastId={message.meta.broadcast_id} onClose={() => setAcksOpen(false)} />
      )}
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent data-testid="delete-message-confirm" className="z-[260]">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("delete_message")}</AlertDialogTitle>
            <AlertDialogDescription>{t("delete_message_confirm")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-col gap-2">
            <AlertDialogAction
              data-testid="delete-message-for-me-btn"
              className="w-full"
              onClick={hideForMe}
            >
              {t("delete_for_me")}
            </AlertDialogAction>
            {canDeleteForAll && (
              <AlertDialogAction
                data-testid="delete-message-confirm-btn"
                className="w-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={deleteMsg}
              >
                {t("delete_for_everyone")}
              </AlertDialogAction>
            )}
            <AlertDialogCancel data-testid="delete-message-cancel" className="w-full mt-0">{t("cancel")}</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
